import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DataTable } from '@/components/DataTable';
import { DatePicker } from '@/components/DatePicker';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import { formatDateTime } from '@/lib/datetime';
import { listPostbackLogs, type PostbackLog } from '@/api/postback-logs';

const PAGE = 50;

export function PostbackLogs() {
  const [direction, setDirection] = useState<'' | 'in' | 'out'>('');
  const [ok, setOk] = useState<'' | '1' | '0'>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<PostbackLog | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['postback-logs', direction, ok, from, to, q, offset],
    queryFn: () => listPostbackLogs({ direction, ok, from, to, q, limit: PAGE, offset }),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const resetAnd = (fn: () => void) => { fn(); setOffset(0); };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold text-content">Tracker · Postback Logs</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">Direction</label>
          <Select
            value={direction}
            onChange={(v) => resetAnd(() => setDirection(v as '' | 'in' | 'out'))}
            options={[
              { value: '', label: 'All' },
              { value: 'in', label: 'Incoming' },
              { value: 'out', label: 'Outgoing' },
            ]}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">Status</label>
          <Select
            value={ok}
            onChange={(v) => resetAnd(() => setOk(v as '' | '1' | '0'))}
            options={[
              { value: '', label: 'All' },
              { value: '1', label: 'OK' },
              { value: '0', label: 'Failed' },
            ]}
          />
        </div>
        <DatePicker id="postback-from" label="From" value={from} onChange={(v) => resetAnd(() => setFrom(v))} />
        <DatePicker id="postback-to" label="To" value={to} onChange={(v) => resetAnd(() => setTo(v))} />
        <div>
          <label htmlFor="postback-search" className="block text-sm font-medium text-content-muted mb-1">Search</label>
          <input id="postback-search" value={q} onChange={(e) => resetAnd(() => setQ(e.target.value))} placeholder="click_id / url"
            className="px-3 py-2 rounded-md bg-surface border border-border text-content text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
        </div>
      </div>

      {error ? (
        <div className="bg-surface border border-border rounded-lg p-6 text-center">
          <p className="text-sm text-danger mb-3">{(error as Error).message}</p>
          <Button variant="secondary" size="sm" onClick={() => refetch()}>Retry</Button>
        </div>
      ) : (
        <>
          <DataTable<PostbackLog>
            loading={isLoading}
            data={rows}
            keyExtractor={(r) => r.id}
            onRowClick={(r) => setSelected(r)}
            columns={[
              { key: 'created_at', label: 'Time', render: (r) => <span className="text-content-muted">{formatDateTime(r.created_at)}</span> },
              {
                key: 'direction', label: 'Dir',
                render: (r) => (
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                    r.direction === 'in' ? 'bg-blue-500/15 text-blue-400' : 'bg-purple-500/15 text-purple-400'
                  }`}>{r.direction === 'in' ? 'IN' : 'OUT'}</span>
                ),
              },
              {
                key: 'ok', label: 'Status',
                render: (r) => (
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                    r.ok ? 'bg-success-soft text-success' : 'bg-danger-soft text-danger'
                  }`}>{r.ok ? 'OK' : 'Failed'}{r.response_status ? ` ${r.response_status}` : ''}</span>
                ),
              },
              { key: 'click_id', label: 'Click / Conv', render: (r) => <span className="font-mono text-xs text-content-muted">{r.click_id || r.conversion_id || '—'}</span> },
              { key: 'endpoint', label: 'Endpoint', render: (r) => { const full = fullRequest(r); return <span className="truncate inline-block max-w-[22rem] align-bottom text-content-muted" title={full || ''}>{full || '—'}</span>; } },
            ]}
          />

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 text-sm text-content-muted">
            <span>{total === 0 ? '0' : `${offset + 1}–${Math.min(offset + PAGE, total)}`} of {total}</span>
            <span className="flex gap-2">
              <Button variant="secondary" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Prev</Button>
              <Button variant="secondary" size="sm" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>Next</Button>
            </span>
          </div>
        </>
      )}

      {/* Detail */}
      <Modal isOpen={!!selected} onClose={() => setSelected(null)} title="Postback log" size="lg">
        {selected && (
          <div className="space-y-3 text-sm">
            <Field label="Direction" value={selected.direction} />
            <Field label="Status" value={selected.ok ? 'OK' : 'Failed'} />
            <Field label="Time" value={formatDateTime(selected.created_at)} />
            <Field label="HTTP status" value={selected.response_status} />
            <Field label="Method" value={selected.method} />
            <Block label="Full request" value={fullRequest(selected)} />
            <ParamList label="Parameters" params={requestParams(selected)} />
            <Field label="Click ID" value={selected.click_id} />
            <Field label="Conversion ID" value={selected.conversion_id} />
            <Field label="Matched source" value={selected.matched_source} />
            <Field label="Attempt" value={selected.attempt} />
            <Field label="Error" value={selected.error} />
            <Block label="Response body" value={selected.response_body} />
          </div>
        )}
      </Modal>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | number | null }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-content-subtle">{label}</span>
      <span className="text-content text-right break-all">{value === null || value === '' ? '—' : String(value)}</span>
    </div>
  );
}

function Block({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <div className="text-content-subtle mb-1">{label}</div>
      <pre className="bg-background border border-border rounded-md p-2 text-xs text-content-muted whitespace-pre-wrap break-all max-h-48 overflow-auto">{value || '—'}</pre>
    </div>
  );
}

// Reconstructs the full request line. Incoming hits store only the path in
// `endpoint` and the query string in `request_payload`; outgoing rows already
// store the full resolved URL in `endpoint`.
function fullRequest(log: PostbackLog): string {
  if (log.direction === 'in') {
    return `${log.endpoint ?? ''}${log.request_payload ?? ''}`;
  }
  return log.endpoint ?? '';
}

// Every parameter of the request, parsed for readable display. Incoming hits and
// outgoing GETs carry them in the query string; outgoing POSTs in the form body.
function requestParams(log: PostbackLog): Array<[string, string]> {
  let raw: string;
  if (log.direction === 'out' && log.method === 'POST') {
    raw = log.request_payload ?? '';
  } else if (log.direction === 'in') {
    raw = log.request_payload ?? '';
  } else {
    raw = (log.endpoint ?? '').split('?').slice(1).join('?');
  }
  const q = raw.indexOf('?');
  if (q >= 0) raw = raw.slice(q + 1);
  if (!raw) return [];
  try {
    return Array.from(new URLSearchParams(raw).entries());
  } catch {
    return [];
  }
}

function ParamList({ label, params }: { label: string; params: Array<[string, string]> }) {
  return (
    <div>
      <div className="text-content-subtle mb-1">{label}</div>
      {params.length === 0 ? (
        <p className="text-content-muted text-xs">—</p>
      ) : (
        <div className="bg-background border border-border rounded-md divide-y divide-border max-h-60 overflow-auto">
          {params.map(([k, v], i) => (
            <div key={`${k}-${i}`} className="flex items-start gap-3 px-2 py-1 text-xs">
              <span className="font-mono text-content-subtle shrink-0">{k}</span>
              <span className="font-mono text-content break-all text-right ml-auto">{v === '' ? '—' : v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
